// ═══════════════════════════════════════════════════════════════════
// cardMotion.js — THE ROUTER · H23 SHIP 1 → H24 · Terror TCG
// One question in, one score out. Every animation in the game is
// resolved through this file and nothing else.
// ═══════════════════════════════════════════════════════════════════
//
// ── WHAT THIS FILE WAS, AND WHAT IT IS NOW ─────────────────────────
// H23 Ship 1 made this the bestiary itself: twelve attacks, one
// species' summons and brands, and the Rite of Return, all written
// inline. That was exactly right for nineteen scores.
//
// H24 authored the other two hundred and eight, and at that size a
// single file is not a file, it is a landfill. So the scores moved out
// to one registry per card class and this became the ROUTER — the thing
// that answers "what does this card do on this channel" and knows
// nothing else. It is under 200 lines and it should stay that way.
//
//   motionKit.js    the palette, the chord builders, the linter
//   beingMotion.js  12 Lyt Attacks · 36 summons · 4 Esclings · the Rite
//   imperaMotion.js 36 brands
//   ultiMotion.js   12 signatures
//   tomeMotion.js   72 Tomes · 9 Curios · 8 Prophecies · 4 Affinities
//   boardMotion.js  29 board verbs — the things that are not cards
//   cardMotion.js   ← you are here. The router, and the public API.
//
// ⚠ EVERY EXPORT THIS FILE HAD IN H23 STILL EXISTS AND STILL MEANS THE
// SAME THING. `Shatterfield3D.js` imports `scoreFor`, `resolveScore`,
// `scoreHoldMs` and `CHANNELS` from here and did not have to change to
// keep working — the four new channels are additive. `LYT_ATTACKS`,
// `SUMMONS`, `ATTACHMENTS` and `RITE_OF_RETURN` are re-exported from
// their new homes so any other consumer (the Codex, the bench,
// balancer) also keeps resolving.
//
// ── THE CONTRACT, UNCHANGED SINCE H23 ──────────────────────────────
// TOTAL. A null card, an unknown channel, a card minted next year —
// every path returns either a usable score or null, and null means
// "the taxonomy floor has this," never "throw." These run one frame
// away from a useFrame; a registry that can throw is a registry that
// can take the board down.
// ═══════════════════════════════════════════════════════════════════

import { scaleMs, getMotion } from "./strikeLanguage";
import {
  lineOf, LYT_ATTACKS, SUMMONS, ESCLING_SUMMONS, RITE_OF_RETURN,
  WOUNDS, woundFor, RETURNS, returnFor, describeBestiary,
} from "./beingMotion";
import { IMPERAS, describeImperas } from "./imperaMotion";
import { ULTIS, describeUltis } from "./ultiMotion";
import {
  TOMES, CURIOS, PROPHECIES, AFFINITIES, describeSpellbook,
} from "./tomeMotion";
import {
  BOARD, boardScore, ladderScore, animaScore, climbInfo, describeBoard,
} from "./boardMotion";

export {
  lineOf, LYT_ATTACKS, SUMMONS, ESCLING_SUMMONS, RITE_OF_RETURN, WOUNDS, woundFor,
  RETURNS, returnFor,
  IMPERAS, ULTIS, TOMES, CURIOS, PROPHECIES, AFFINITIES,
  BOARD, boardScore, ladderScore, animaScore, climbInfo,
};

// ── THE ATTACHMENT LADDER, AS ONE TABLE ────────────────────────────
// Impera → U'hlti → Affinity all land on a being's Soul, never in a
// rift, and the renderer diffs them as one event. So they resolve as
// one table here. The three id prefixes (`p_`, `u_`, `aff_`) can never
// collide, which is what makes the merge safe rather than merely
// convenient.
export const ATTACHMENTS = { ...IMPERAS, ...ULTIS, ...AFFINITIES };

// ═══════════════════════════════════════════════════════════════════
// THE CHANNELS
// ═══════════════════════════════════════════════════════════════════
// One event, one channel — H20's discipline, kept. The first four are
// H23's and are wired through the transition detector. The four added
// in H24 cover the card classes that had no moment of their own:
//
//   SUMMON   a being takes a rift              (detector · on touchdown)
//   LYT      a being swings                    (detector · attackedThisTurn)
//   RETURN   the ground gives a being back     (detector · necro shrinks)
//   ATTACH   a brand, a signature, or a seal   (detector · soul grows)
//   CAST     a Tome ACTIVATES                  (H24 · the reveal, not the set)
//   OFFER    a Curio is acknowledged           (H24 · usually at duel start)
//   DECLARE  a Prophecy is declared            (H24 · the face-down turns)
//   RUIN     a card is destroyed               (H24 · generic, per type)
//
// ⚠ CAST FIRES ON REVEAL, NEVER ON PLACEMENT. A face-down Tome is a
// card being SET; the Casting's rune would announce spellwork that has
// not happened AND leak the school across the seam on a card the
// adversary is not allowed to read. Shatterfield3D's detector already
// carries that rule for the flight (`faceDownLand`) and the same rule
// governs this channel.
export const CHANNELS = {
  SUMMON: "summon",
  LYT: "lyt",
  RETURN: "return",
  ATTACH: "attach",
  CAST: "cast",
  OFFER: "offer",
  DECLARE: "declare",
  RUIN: "ruin",
  // H24 pass 3 — the wound the VICTIM shows, keyed by its own species.
  // H21's reasoning holds and is why this is the victim's and not the
  // attacker's: it stays derivable from one card's state diff, so it
  // fires identically on every client with no bus and no extra write.
  HURT: "hurt",
};

/**
 * The score for a card on a channel, or null if nothing is authored
 * (in which case H21's arrival/strike profile is the whole answer,
 * exactly as it was before any of this existed).
 */
export function scoreFor(card, channel) {
  if (!card || !card.id) return null;
  switch (channel) {
    case CHANNELS.LYT:
      return LYT_ATTACKS[lineOf(card)] || null;

    case CHANNELS.SUMMON:
      // Esclings are placed before the duel and are not in SUMMONS —
      // they get the Seeding, which is its own small table.
      return SUMMONS[card.id] || ESCLING_SUMMONS[card.id] || null;

    case CHANNELS.ATTACH:
      return ATTACHMENTS[card.id] || null;

    case CHANNELS.CAST:
      return TOMES[card.id] || null;

    case CHANNELS.OFFER:
      return CURIOS[card.id] || null;

    case CHANNELS.DECLARE:
      return PROPHECIES[card.id] || null;

    case CHANNELS.HURT:
      return woundFor(card);

    case CHANNELS.RUIN:
      // Ruin is keyed by what KIND of thing died, not by which card —
      // authoring 181 death animations would be absurd and would also
      // read worse, because the player wants one recognisable image for
      // "that is gone." The one distinction worth drawing is whether
      // the Soul went with it, and only a handful of cards can do that.
      return BOARD.ruin;

    case CHANNELS.RETURN:
      // H24 pass 5 — SPECIES FIRST, then the School tint, then base.
      // `returnFor` owns that ladder so no caller can get the order
      // wrong; the H23 tints are still the answer for anything outside
      // the twelve lines. (The tint axis is Space/Phantom/Dragon/Time,
      // which the 2026-08-25 ruling names SCHOOL; returnFor reads
      // `card.school` and the note that used to live here is retired.)
      return returnFor(card);

    default:
      return null;
  }
}

/**
 * The channel a card would naturally resolve on when a caller knows an
 * effect fired but not which door it came through. Used by the H23
 * trigger bus (`fireMotion`), where a handler in Shatterfield.js can
 * say "this card just did its thing" without also having to know the
 * animation system's taxonomy.
 *
 * THE EXCEPTION LAW, applied: this is a mapping off `card.type`, which
 * is DATA. A ninth card type minted next year picks its channel by
 * adding one row here, not by editing a switch in the renderer.
 */
const TYPE_CHANNEL = {
  Escendant: CHANNELS.SUMMON,
  Escling: CHANNELS.SUMMON,
  Tome: CHANNELS.CAST,
  Curio: CHANNELS.OFFER,
  Prophecy: CHANNELS.DECLARE,
  Impera: CHANNELS.ATTACH,
  "U'hlti": CHANNELS.ATTACH,
  Affinity: CHANNELS.ATTACH,
};
export const channelFor = (card) =>
  (card && TYPE_CHANNEL[card.type]) || CHANNELS.ATTACH;

/** Does this card have anything authored at all? (Codex / bench use.) */
export const isAuthored = (card) => {
  if (!card || !card.id) return false;
  return !!(LYT_ATTACKS[lineOf(card)] || SUMMONS[card.id] ||
    ESCLING_SUMMONS[card.id] || ATTACHMENTS[card.id] ||
    TOMES[card.id] || CURIOS[card.id] || PROPHECIES[card.id]);
};

/**
 * Turn a written score into live cues.
 *
 * ctx = { self:[x,y,z], target:[x,y,z], id, tier }
 *   self    where the acting card sits
 *   target  the victim, or the enemy plate when nothing was wounded
 *   id      a unique prefix so two simultaneous chords never collide
 *
 * Anchors resolve HERE and nowhere else, which is what lets one score
 * play from either chair and from any of the three rifts without a
 * single per-seat branch. `sky` is deliberately generous (4.6 up) — a
 * pillar that starts inside the camera frustum reads as a lamp.
 */
export function resolveScore(score, ctx) {
  if (!score || !score.cues) return [];
  const self = ctx?.self || [0, 0, 0];
  const target = ctx?.target || self;
  const seam = [(self[0] + target[0]) / 2, Math.max(self[1], target[1]) + 0.35, (self[2] + target[2]) / 2];
  const back = [self[0] * 1.12, self[1], self[2] + (self[2] >= 0 ? 1.1 : -1.1)];
  const at = (tok, fallback) => {
    if (Array.isArray(tok)) return tok;
    switch (tok) {
      case "self": return self;
      case "target": return target;
      case "seam": return seam;
      case "back": return back;
      case "sky": return [target[0], target[1] + 4.6, target[2]];
      case "selfsky": return [self[0], self[1] + 4.6, self[2]];
      default: return fallback;
    }
  };
  const pre = ctx?.id || "s";
  return score.cues.map((c, i) => ({
    ...c,
    id: `${pre}|${i}`,
    from: at(c.from, self),
    to: at(c.to, c.from ? at(c.from, self) : self),
  }));
}

/**
 * How long the board should hold its breath for this score, in ms,
 * already scaled by the dial. Returns 0 at OFF — and the caller must
 * read 0 as "don't mount it", never as "play it instantly".
 */
export function scoreHoldMs(score) {
  if (!score) return 0;
  return scaleMs(score.ms || 700, getMotion());
}

// ── THE CODEX VIEW ─────────────────────────────────────────────────
// The whole library as one readable table, for a Rulebook chapter, a
// settings preview, or the motion bench. Nothing in the duel path reads
// this.
export function describeLibrary() {
  return [].concat(
    describeBestiary(), describeImperas(), describeUltis(),
    describeSpellbook(), describeBoard(),
  );
}
export { describeBestiary };

/** How many scores exist, by channel. Used by the bench header. */
export function libraryCount() {
  const n = (o) => Object.keys(o).length;
  return {
    lyt: n(LYT_ATTACKS), wound: n(WOUNDS), rebirth: n(RETURNS),
    summon: n(SUMMONS) + n(ESCLING_SUMMONS),
    impera: n(IMPERAS), ulhti: n(ULTIS), affinity: n(AFFINITIES),
    tome: n(TOMES), curio: n(CURIOS), prophecy: n(PROPHECIES),
    board: n(BOARD), rite: n(RITE_OF_RETURN),
    total: n(LYT_ATTACKS) + n(WOUNDS) + n(RETURNS) + n(SUMMONS) + n(ESCLING_SUMMONS) +
      n(IMPERAS) + n(ULTIS) + n(AFFINITIES) + n(TOMES) + n(CURIOS) +
      n(PROPHECIES) + n(BOARD) + n(RITE_OF_RETURN),
  };
}

const cardMotion = {
  LYT_ATTACKS, SUMMONS, ESCLING_SUMMONS, ATTACHMENTS, RITE_OF_RETURN,
  IMPERAS, ULTIS, TOMES, CURIOS, PROPHECIES, AFFINITIES, BOARD,
  WOUNDS, woundFor,
  CHANNELS, lineOf, scoreFor, channelFor, resolveScore, scoreHoldMs,
  isAuthored, boardScore, ladderScore, animaScore, climbInfo,
  RETURNS, returnFor,
  describeBestiary, describeLibrary, libraryCount,
};
export default cardMotion;
